import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface UiState {
  logo: string | null;
  primaryColor: string;
  secondaryColor: string | null;
}

const initialState: UiState = {
  logo: null,
  primaryColor: '#FE0000', // Default Finloc red color
  secondaryColor: '#FFFFFF', // Default white color for text
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setLogo: (state, action: PayloadAction<string | null>) => {
      state.logo = action.payload;
      // Also store in localStorage
      if (action.payload) {
        localStorage.setItem('appLogo', action.payload);
      } else {
        localStorage.removeItem('appLogo');
      }
    },
    loadLogoFromStorage: (state) => {
      const storedLogo = localStorage.getItem('appLogo');
      if (storedLogo) {
        state.logo = storedLogo;
      }
    },
    setPrimaryColor: (state, action: PayloadAction<string>) => {
      state.primaryColor = action.payload;
      // Store in localStorage
      localStorage.setItem('appPrimaryColor', action.payload);
      // Apply to CSS variable for global use
      if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty('--color-primary', action.payload);
      }
    },
    loadPrimaryColorFromStorage: (state) => {
      const storedColor = localStorage.getItem('appPrimaryColor');
      if (storedColor) {
        state.primaryColor = storedColor;
        // Apply to CSS variable
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--color-primary', storedColor);
        }
      } else {
        // Ensure default is used if no stored color
        state.primaryColor = initialState.primaryColor;
        // Apply default color to CSS variable
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--color-primary', initialState.primaryColor);
        }
        // Save default to localStorage for consistency
        localStorage.setItem('appPrimaryColor', initialState.primaryColor);
      }
    },
    setSecondaryColor: (state, action: PayloadAction<string>) => {
      state.secondaryColor = action.payload;
      // Store in localStorage
      localStorage.setItem('appSecondaryColor', action.payload);
      // Apply to CSS variable for global use
      if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty('--color-secondary', action.payload);
      }
    },
    loadSecondaryColorFromStorage: (state) => {
      const storedColor = localStorage.getItem('appSecondaryColor');
      if (storedColor) {
        state.secondaryColor = storedColor;
        // Apply to CSS variable
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--color-secondary', storedColor);
        }
      } else {
        // Ensure default is used if no stored color
        state.secondaryColor = initialState.secondaryColor;
        // Apply default color to CSS variable
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--color-secondary', initialState.secondaryColor || '#FFFFFF');
        }
        // Save default to localStorage for consistency
        if (initialState.secondaryColor) {
          localStorage.setItem('appSecondaryColor', initialState.secondaryColor);
        }
      }
    },
  },
});

export const { setLogo, loadLogoFromStorage, setPrimaryColor, loadPrimaryColorFromStorage, setSecondaryColor, loadSecondaryColorFromStorage } = uiSlice.actions;
export default uiSlice.reducer;